// Connector layer for the tech tree — the arrows + dashed sibling link, lifted
// verbatim from the Figma export SVG (node 666:2031) so the geometry matches the
// design exactly. The box rects + text glyphs from that SVG are intentionally
// dropped here: the boxes are real interactive HTML elements rendered on top of
// this layer (see TechTree). Purely presentational; ignores pointer events. The
// viewBox is the design's native 1033×450 space, so it scales with the container
// while the boxes are positioned in the same units.
//
// `layer` splits the rendering across two passes so one wire can sit ABOVE the
// boxes per the design: "base" (default) draws every arrow except the long
// right-hand LINE LIFF↔Stripe curve and goes behind the boxes; "over" draws only
// that curve and is rendered after the boxes so it overlaps them.
export default function Connectors({ layer = "base", className = "" }) {
  const over = layer === "over";
  return (
    <svg
      viewBox="0 0 1033 450"
      fill="none"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    >
      {over ? (
        /* LINE LIFF → Stripe (long right curve) — drawn on top of the boxes */
        <path
          d="M991.713 359.661L991.374 360.33L992.713 361.008L993.051 360.339L992.382 360L991.713 359.661ZM961.849 199.265C961.443 199.349 961.182 199.746 961.265 200.151L962.628 206.762C962.711 207.168 963.108 207.429 963.514 207.346C963.919 207.262 964.181 206.865 964.097 206.46L962.886 200.583L968.762 199.372C969.168 199.289 969.429 198.892 969.346 198.486C969.262 198.081 968.865 197.819 968.46 197.903L961.849 199.265ZM992.382 360L993.051 360.339C1023.57 300.078 1008.37 229.629 962.412 199.374L962 200L961.588 200.626C1006.77 230.371 1021.96 299.922 991.713 359.661L992.382 360Z"
          fill="#9C9A92"
        />
      ) : (
        <>
          {/* React → Web Application (up) */}
      <path
        d="M403.53 94.4697C403.237 94.1768 402.763 94.1768 402.47 94.4697L397.697 99.2426C397.404 99.5355 397.404 100.01 397.697 100.303C397.99 100.596 398.464 100.596 398.757 100.303L403 96.0607L407.243 100.303C407.536 100.596 408.01 100.596 408.303 100.303C408.596 100.01 408.596 99.5355 408.303 99.2426L403.53 94.4697ZM403 140H403.75V95H403H402.25V140H403Z"
        fill="#9C9A92"
      />
      {/* LINE LIFF → Web Application (up) */}
      <path
        d="M630.53 94.4697C630.237 94.1768 629.763 94.1768 629.47 94.4697L624.697 99.2426C624.404 99.5355 624.404 100.01 624.697 100.303C624.99 100.596 625.464 100.596 625.757 100.303L630 96.0607L634.243 100.303C634.536 100.596 635.01 100.596 635.303 100.303C635.596 100.01 635.596 99.5355 635.303 99.2426L630.53 94.4697ZM630 140H630.75V95H630H629.25V140H630Z"
        fill="#9C9A92"
      />
      {/* React → Node.js (curve in) */}
      <path
        d="M462.581 245V245.75H464.081V245H463.331H462.581ZM402.586 199.25C402.172 199.24 401.827 199.566 401.817 199.981L401.642 206.728C401.631 207.142 401.958 207.487 402.372 207.497C402.786 207.508 403.13 207.181 403.141 206.767L403.297 200.769L409.295 200.925C409.709 200.935 410.053 200.608 410.064 200.194C410.075 199.78 409.748 199.436 409.334 199.425L402.586 199.25ZM463.331 245H464.081C464.081 237.296 461.387 231.789 457.028 227.585C452.701 223.414 446.767 220.558 440.342 218.051C433.845 215.517 426.961 213.376 420.366 210.56C413.818 207.764 407.755 204.376 403.11 199.484L402.566 200L402.023 200.516C406.872 205.624 413.152 209.111 419.777 211.94C426.357 214.749 433.476 216.983 439.797 219.449C446.19 221.942 451.886 224.711 455.986 228.665C460.053 232.586 462.581 237.704 462.581 245H463.331Z"
        fill="#9C9A92"
      />
      {/* LINE LIFF → Node.js (curve in) */}
      <path
        d="M568.919 245V245.75H570.419V245H569.669H568.919ZM631.184 199.981C631.173 199.566 630.829 199.24 630.414 199.25L623.667 199.425C623.253 199.436 622.926 199.78 622.936 200.194C622.947 200.608 623.292 200.935 623.706 200.925L629.704 200.769L629.859 206.767C629.87 207.181 630.214 207.508 630.628 207.497C631.042 207.487 631.369 207.142 631.359 206.728L631.184 199.981ZM569.669 245H570.419C570.419 237.704 572.947 232.586 577.014 228.665C581.114 224.711 586.811 221.942 593.203 219.449C599.524 216.983 606.644 214.749 613.223 211.94C619.849 209.111 626.128 205.624 630.978 200.516L630.434 200L629.89 199.484C625.245 204.376 619.182 207.764 612.634 210.56C606.04 213.376 599.155 215.517 592.658 218.051C586.233 220.558 580.299 223.414 575.973 227.585C571.613 231.789 568.919 237.296 568.919 245H569.669Z"
        fill="#9C9A92"
      />
      {/* MongoDB → Node.js (up) */}
      <path
        d="M125.53 314.47C125.237 314.177 124.763 314.177 124.47 314.47L119.697 319.243C119.404 319.536 119.404 320.01 119.697 320.303C119.99 320.596 120.464 320.596 120.757 320.303L125 316.061L129.243 320.303C129.536 320.596 130.01 320.596 130.303 320.303C130.596 320.01 130.596 319.536 130.303 319.243L125.53 314.47ZM125 360H125.75V315H125H124.25V360H125Z"
        fill="#9C9A92"
      />
      {/* AWS → Node.js (up) */}
      <path
        d="M517.53 314.47C517.237 314.177 516.763 314.177 516.47 314.47L511.697 319.243C511.404 319.536 511.404 320.01 511.697 320.303C511.99 320.596 512.464 320.596 512.757 320.303L517 316.061L521.243 320.303C521.536 320.596 522.01 320.596 522.303 320.303C522.596 320.01 522.596 319.536 522.303 319.243L517.53 314.47ZM517 360H517.75V315H517H516.25V360H517Z"
        fill="#9C9A92"
      />
      {/* Stripe → Node.js (up) */}
      <path
        d="M909.53 314.47C909.237 314.177 908.763 314.177 908.47 314.47L903.697 319.243C903.404 319.536 903.404 320.01 903.697 320.303C903.99 320.596 904.464 320.596 904.757 320.303L909 316.061L913.243 320.303C913.536 320.596 914.01 320.596 914.303 320.303C914.596 320.01 914.596 319.536 914.303 319.243L909.53 314.47ZM909 360H909.75V315H909H908.25V360H909Z"
        fill="#9C9A92"
      />
          {/* MongoDB ⋯ AWS (dashed sibling link) */}
          <path
            opacity="0.55"
            d="M250 388H392"
            stroke="#9C9A92"
            strokeDasharray="4 4"
          />
        </>
      )}
    </svg>
  );
}
